import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";
import { Sparkles, ShieldCheck, Info } from "lucide-react";

/**
 * Public /ai-disclosure — content-driven so legal can edit later.
 * No feature flag gate needed; this is documentation only.
 */
export default function AIDisclosure() {
    const [status, setStatus] = useState(null);
    useEffect(() => {
        axios.get(process.env.REACT_APP_BACKEND_URL + "/api/ai/status", { withCredentials: true })
            .then((r) => setStatus(r.data)).catch(() => {});
    }, []);
    return (
        <div className="min-h-screen bg-stone-50 text-stone-900">
            <div className="max-w-3xl mx-auto px-6 pt-10 pb-24">
                <div className="flex items-center justify-between mb-10">
                    <Link to="/"><BrandLogo height={32} /></Link>
                    <div className="flex items-center gap-3 text-[12.5px]">
                        <Link to="/trust" className="text-stone-600 hover:text-stone-900">Trust Centre</Link>
                        <Link to="/status" className="text-stone-600 hover:text-stone-900">Status</Link>
                    </div>
                </div>

                <div className="mb-10">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-bold uppercase tracking-widest">
                        <Sparkles size={11} /> VAULT Intelligence · AI
                    </div>
                    <h1 className="font-display text-5xl leading-none mt-4">
                        How VAULT Intelligence Works
                    </h1>
                    <p className="text-base text-stone-600 mt-5 max-w-2xl">
                        A clear, honest overview of what our AI does — and just as importantly,
                        what it does not do.
                    </p>
                </div>

                <Section title="What VAULT Intelligence is">
                    <p>An intelligent assistant that helps you understand, locate, organise and
                    summarise information already inside your own VAULT. It can answer questions
                    like <em>“which subscriptions renew this month?”</em>, find related documents,
                    and highlight records that need updating.</p>
                </Section>

                <Section title="What VAULT Intelligence is NOT" tone="rose">
                    <p>It is not a financial adviser, investment adviser, tax adviser, insurance
                    adviser, credit adviser or trading assistant. It will not recommend stocks,
                    mutual funds, insurance policies or loans, and it will not tell you what to
                    buy, sell or hold.</p>
                </Section>

                <Section title="Privacy controls you have">
                    <ul className="space-y-1.5 list-disc pl-5">
                        <li>Turn VAULT Intelligence on or off at any time</li>
                        <li>Enable or disable document / statement / financial-record analysis independently</li>
                        <li>Clear or delete your entire AI conversation history</li>
                        <li>Download your AI interaction history</li>
                        <li>Opt-out of personalised suggestions</li>
                        <li>Model training on your data is <strong>off by default</strong></li>
                    </ul>
                </Section>

                <Section title="What we never send to AI providers" id="insights">
                    <ul className="space-y-1.5 list-disc pl-5">
                        <li>Full card numbers, CVV/CVC, PIN, OTP</li>
                        <li>Online banking passwords or credentials</li>
                        <li>MFA secrets, recovery codes, API keys, session tokens</li>
                        <li>Records from another workspace than the one you&rsquo;re viewing</li>
                    </ul>
                    <p className="mt-3 text-[13px] text-stone-500 flex items-start gap-2">
                        <ShieldCheck size={13} className="mt-0.5 shrink-0" />
                        Before any text reaches an AI provider it passes through a regex
                        sensitive-data scanner. Detected secrets abort the request.
                    </p>
                </Section>

                <Section title="Possible inaccuracies">
                    <p>AI-generated responses may contain errors. VAULT distinguishes retrieved
                    facts from AI interpretation and never invents amounts, dates or record
                    names. Where records are incomplete, VAULT Intelligence tells you so instead
                    of guessing.</p>
                </Section>

                <Section title="Feature availability">
                    <p>VAULT Intelligence is currently in a controlled rollout. Its status for
                    you right now:</p>
                    <div className="mt-3 rounded-xl border border-stone-200 bg-white p-4 flex items-center gap-2 text-[13px]">
                        <Info size={13} />
                        <span>
                            {!status ? "Checking…" : status.enabled ? "Available on this workspace." : "Not yet enabled for your workspace."}
                        </span>
                    </div>
                </Section>

                <Section title="Report a concern">
                    <p>If a response ever exposes something it shouldn&rsquo;t or feels wrong, please
                    email <a className="text-amber-700 underline" href="mailto:security@vaultbypd.com">security@vaultbypd.com</a>.
                    We treat AI safety reports with the same urgency as any security disclosure.</p>
                </Section>
            </div>
        </div>
    );
}

function Section({ title, children, tone, id }) {
    const toneCls = tone === "rose" ? "bg-rose-50 border-rose-200" : "bg-white border-stone-200";
    return (
        <section id={id} className={`mt-8 rounded-2xl border p-6 ${toneCls}`}>
            <h2 className="text-[18px] font-semibold text-stone-900 mb-3">{title}</h2>
            <div className="text-[14px] text-stone-700 leading-relaxed">{children}</div>
        </section>
    );
}
